import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_DATA, SITE_URL } from '../seo/seoData';

const buildJsonLd = (d, url) => {
  const blocks = [];
  if (d.path === '/') {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'DevTools',
      url: SITE_URL,
      description: d.description,
    });
  }
  if (d.name) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: d.name,
      url,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Any',
      description: d.description,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    });
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'DevTools', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: d.name, item: url },
      ],
    });
  }
  if (d.faqs && d.faqs.length) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: d.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  return blocks;
};

const ToolSEO = ({ slug }) => {
  const d = SEO_DATA[slug];
  if (!d) return null;
  const url = `${SITE_URL}${d.path}`;
  const jsonLd = buildJsonLd(d, url);

  return (
    <>
      <Helmet>
        <title>{d.title}</title>
        <meta name="description" content={d.description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="DevTools" />
        <meta property="og:title" content={d.title} />
        <meta property="og:description" content={d.description} />
        <meta property="og:url" content={url} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={d.title} />
        <meta name="twitter:description" content={d.description} />
        {jsonLd.map((block, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(block)}
          </script>
        ))}
      </Helmet>

      {d.intro && (
        <section
          data-testid={`seo-content-${slug}`}
          className="bg-white border-t border-gray-200 px-4 py-12"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-medium text-gray-800 mb-3">
              About the {d.name}
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">{d.intro}</p>

            <div className="mb-8">
              <h2 className="text-base font-medium text-gray-800 mb-2">Features</h2>
              <ul className="list-disc pl-5 space-y-1">
                {d.features.map((f, i) => (
                  <li key={i} className="text-gray-600 text-sm">{f}</li>
                ))}
              </ul>
            </div>

            <h2 className="text-base font-medium text-gray-800 mb-3">
              Frequently Asked Questions
            </h2>
            <div className="space-y-2">
              {d.faqs.map((f, i) => (
                <details
                  key={i}
                  data-testid={`faq-item-${slug}-${i}`}
                  className="bg-stone-50 border border-stone-200 rounded-lg px-4 py-3 group"
                >
                  <summary className="text-sm font-medium text-gray-800 cursor-pointer list-none flex items-center justify-between">
                    {f.q}
                    <span className="text-gray-400 text-xs ml-4 transition-transform duration-200 group-open:rotate-180">▼</span>
                  </summary>
                  <p className="text-gray-600 text-sm leading-relaxed mt-2">{f.a}</p>
                </details>
              ))}
            </div>

            <p className="text-gray-500 text-xs mt-8">
              🔒 Privacy first: all tools on this site run 100% in your browser. Your data is never uploaded to any server.
            </p>
          </div>
        </section>
      )}
    </>
  );
};

export default ToolSEO;
